/**
 * Enhanced Error Handler for Authentication Failures
 * Provides user-friendly multiple error handling with progressive warnings
 */

import { frontendLogger } from '../../services/frontendLogger.js';

class AuthErrorManager {
  constructor() {
    this.errorCounts = new Map(); // Track errors per endpoint
    this.maxErrors = 7; // Max consecutive errors before forced logout (realistic for web apps)
    this.warningThreshold = 4; // Show warning after 4 errors
    this.resetTime = 5 * 60 * 1000; // 5 minutes - reset error count window
    this.warningCallbacks = new Set();
    this.logoutCallbacks = new Set();
    
    // Clear error counts periodically
    setInterval(() => this.cleanupOldErrors(), 600000); // Every 10 minutes - PERFORMANCE FIX
  }

  // Subscribe to warning events (when errors accumulate)
  onWarning(callback) {
    this.warningCallbacks.add(callback);
    return () => this.warningCallbacks.delete(callback);
  }

  // Subscribe to forced logout events
  onForceLogout(callback) {
    this.logoutCallbacks.add(callback);
    return () => this.logoutCallbacks.delete(callback);
  }

  // Record an authentication error
  recordAuthError(endpoint) {
    const now = Date.now();
    const key = endpoint || 'general';
    
    if (!this.errorCounts.has(key)) {
      this.errorCounts.set(key, []);
    }
    
    const errors = this.errorCounts.get(key);
    errors.push(now);
    
    // Keep only recent errors (within reset time)
    const recentErrors = errors.filter(timestamp => now - timestamp < this.resetTime);
    this.errorCounts.set(key, recentErrors);
    
    frontendLogger.warn('Auth error recorded', {
      endpoint: key,
      errorCount: recentErrors.length,
      maxErrors: this.maxErrors,
      warningThreshold: this.warningThreshold
    }, 'AuthErrorManager');
    
    // Check if we should take action
    this.checkErrorThreshold(key, recentErrors.length);
  }

  // Check if error threshold is reached
  checkErrorThreshold(endpoint, errorCount) {
    if (errorCount >= this.maxErrors) {
      // 8+ errors - force logout with explanation
      this.notifyForceLogout(endpoint, errorCount);
    } else if (errorCount >= this.warningThreshold) {
      // 5+ errors - show warning
      this.notifyWarning(endpoint, errorCount);
    }
  }

  // Notify warning callbacks
  notifyWarning(endpoint, errorCount) {
    const remainingAttempts = this.maxErrors - errorCount;
    
    this.warningCallbacks.forEach(callback => {
      try {
        callback({
          type: 'auth_warning',
          endpoint,
          errorCount,
          remainingAttempts,
          message: `Authentication issues detected. ${remainingAttempts} attempt(s) remaining before automatic logout.`
        });
      } catch (error) { 
        frontendLogger.error('Auth warning callback error', {
          error: error.message,
          stack: error.stack,
          endpoint,
          errorCount
        }, 'AuthErrorManager');
      }
    });
  }

  // Notify force logout callbacks
  notifyForceLogout(endpoint, errorCount) {
    this.logoutCallbacks.forEach(callback => {
      try {
        callback({
          type: 'force_logout',
          endpoint,
          errorCount,
          reason: 'multiple_auth_errors',
          message: `Multiple authentication failures detected (${errorCount}). For security, you will be logged out.`
        });
      } catch (error) { 
        frontendLogger.error('Auth logout callback error', {
          error: error.message,
          stack: error.stack,
          endpoint,
          errorCount
        }, 'AuthErrorManager');
      }
    });
    
    // Clear error counts after logout
    this.clearAllErrors();
  }

  // Clear errors for specific endpoint
  clearErrors(endpoint) {
    this.errorCounts.delete(endpoint || 'general');
  }

  // Clear all error counts
  clearAllErrors() {
    this.errorCounts.clear();
  }

  // Clean up old error counts
  cleanupOldErrors() {
    const now = Date.now();
    
    for (const [key, errors] of this.errorCounts.entries()) {
      const recentErrors = errors.filter(timestamp => now - timestamp < this.resetTime);
      
      if (recentErrors.length === 0) {
        this.errorCounts.delete(key);
      } else {
        this.errorCounts.set(key, recentErrors);
      }
    }
  }

  // Get current error count for endpoint
  getErrorCount(endpoint) {
    const errors = this.errorCounts.get(endpoint || 'general') || [];
    const now = Date.now();
    return errors.filter(timestamp => now - timestamp < this.resetTime).length;
  }

  // Check if endpoint is in warning state
  isInWarningState(endpoint) {
    return this.getErrorCount(endpoint) >= this.warningThreshold;
  }

  // Manual reset (for successful operations)
  resetErrors(endpoint) {
    if (endpoint) {
      this.clearErrors(endpoint);
    } else {
      this.clearAllErrors();
    }
  }
}

// Export singleton instance
export const authErrorManager = new AuthErrorManager();
export default authErrorManager;